import { api } from './client';
import type {
  BirthdayReport,
  CompletenessEntityType,
  CompletenessRule,
  CompletenessRuleCreateInput,
  CompletenessRuleUpdateInput,
  ExpiringReport,
  FieldMissingReport,
  IncompleteReport,
  Page,
  UUID,
} from './types';

export const completenessRulesApi = {
  list: (entity_type?: CompletenessEntityType): Promise<Page<CompletenessRule>> => {
    const params = new URLSearchParams({ limit: '200' });
    if (entity_type) params.append('entity_type', entity_type);
    return api.get(`/completeness-rules?${params}`);
  },
  create: (input: CompletenessRuleCreateInput): Promise<CompletenessRule> =>
    api.post('/completeness-rules', input),
  update: (id: UUID, input: CompletenessRuleUpdateInput): Promise<CompletenessRule> =>
    api.patch(`/completeness-rules/${id}`, input),
  delete: (id: UUID): Promise<CompletenessRule> =>
    api.delete(`/completeness-rules/${id}`),
};

/** Which records a Compliance list covers (C-356): the whole firm, or "my" clients. */
export type ComplianceScope = 'all' | 'mine';

export const complianceApi = {
  // C-356 (2026-10-11): scope 'all' = firm-wide (Admin / Compliance only; others get 403);
  // 'mine' = the caller's Named-RM agreements and their records ("My follow-ups").
  incomplete: (
    entity_type?: CompletenessEntityType,
    include_inactive: boolean = false,
    scope: ComplianceScope = 'all',
  ): Promise<IncompleteReport> => {
    const params = new URLSearchParams();
    if (entity_type) params.append('entity_type', entity_type);
    if (include_inactive) params.append('include_inactive', 'true');
    if (scope === 'mine') params.append('scope', 'mine');
    const qs = params.toString();
    return api.get(`/compliance/incomplete${qs ? `?${qs}` : ''}`);
  },
  fieldsMissing: (
    entity_type: CompletenessEntityType,
    id: UUID,
  ): Promise<FieldMissingReport> =>
    api.get(`/compliance/incomplete-fields/${entity_type}/${id}`),
  expiring: (
    within_months: number,
    include_inactive: boolean = false,
    scope: ComplianceScope = 'all',
  ): Promise<ExpiringReport> => {
    const params = new URLSearchParams({ within_months: String(within_months) });
    if (include_inactive) params.append('include_inactive', 'true');
    if (scope === 'mine') params.append('scope', 'mine');
    return api.get(`/compliance/expiring?${params}`);
  },
  /** P11: every person whose birthday falls in the next `within_days` days (rolling). */
  birthdays: (within_days: number = 30): Promise<BirthdayReport> =>
    api.get(`/compliance/birthdays?within_days=${within_days}`),
};
